/**
 * Milestone Tracking Workflow
 *
 * This workflow monitors project milestone progress:
 * 1. Detects milestone creation/updates
 * 2. Analyzes progress of milestone tasks
 * 3. Compares actual vs expected progress
 * 4. Alerts on risks and delays
 */

import type { Workflow } from './types';

// =============================================================================
// WORKFLOW DEFINITION
// =============================================================================

export const milestoneTrackingWorkflow: Workflow = {
  id: 'milestone-tracking',
  name: 'Monitoraggio Milestone',
  description: 'Monitora automaticamente il progresso delle milestone, confronta il progresso reale con quello atteso e avvisa in caso di ritardi o rischi',
  trigger: {
    type: 'event',
    config: {
      // Trigger on milestone creation or update
      eventTypes: ['entity.created', 'entity.updated'],
      entityType: 'milestone',
      // Also trigger on task updates within milestone
      includeRelatedEntities: ['task'],
    },
  },
  steps: [
    {
      id: 'check_progress',
      name: 'Verifica Progresso',
      type: 'ai_analyze',
      config: {
        prompt: `Analizza il progresso di questa milestone.

Valuta:
- Percentuale task completati vs totale
- Data di completamento attesa vs data target
- Velocità di completamento task (task/giorno)
- Task critici ancora aperti
- Blocchi o dipendenze irrisolte

Rispondi in formato JSON:
{
  "totalTasks": number,
  "completedTasks": number,
  "completionPercentage": number,
  "targetDate": "ISO date",
  "estimatedCompletionDate": "ISO date",
  "daysAhead": number (positivo = in anticipo, negativo = in ritardo),
  "completionVelocity": number,
  "criticalTasksOpen": number,
  "blockedTasks": number,
  "riskLevel": "low" | "medium" | "high"
}`,
        includeMilestoneContext: true,
        includeProjectContext: true,
      },
      next: ['detect_risk'],
    },
    {
      id: 'detect_risk',
      name: 'Rileva Rischi',
      type: 'custom',
      config: {
        // Compare actual vs expected progress
        action: 'calculate_milestone_risk',
        thresholds: {
          // Alert if more than 2 days behind
          delayThresholdDays: 2,
          // Alert if velocity drops below 50% of target
          velocityThresholdPercent: 50,
          // Alert if critical tasks still open at 80% milestone progress
          criticalTaskAlertAt: 0.8,
        },
      },
      next: ['send_notification'],
      condition: {
        field: 'riskLevel',
        operator: 'greater',
        value: 'low',
      },
    },
    {
      id: 'send_notification',
      name: 'Avvisa Rischi',
      type: 'send_notification',
      config: {
        type: 'in_app',
        message: 'Attenzione: la milestone "{milestoneName}" è {daysAhead} giorni {status}. Rischio: {riskLevel}. {criticalTasksOpen} task critici ancora aperti.',
        priority: 'high',
        includeMilestoneLink: true,
        includeProjectLink: true,
        action: {
          label: 'Vedi Dettagli',
          handler: 'view_milestone_details',
        },
      },
    },
  ],
  enabled: true,
  version: 1,
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
};

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Calculate milestone risk level
 */
export function calculateMilestoneRisk(
  daysAhead: number,
  completionPercentage: number,
  criticalTasksOpen: number,
  blockedTasks: number
): 'low' | 'medium' | 'high' {
  // High risk: behind schedule OR many critical tasks open
  if (daysAhead < -2 || (completionPercentage > 80 && criticalTasksOpen > 2)) {
    return 'high';
  }

  // Medium risk: slightly behind OR some critical tasks OR blocked tasks
  if (daysAhead < 0 || criticalTasksOpen > 0 || blockedTasks > 1) {
    return 'medium';
  }

  return 'low';
}

/**
 * Estimate completion date based on velocity
 */
export function estimateCompletionDate(
  remainingTasks: number,
  completionVelocity: number,
  currentDate: Date = new Date()
): Date {
  if (completionVelocity <= 0) {
    // No velocity data, return far future date
    const farFuture = new Date(currentDate);
    farFuture.setDate(farFuture.getDate() + 365);
    return farFuture;
  }

  const daysRemaining = Math.ceil(remainingTasks / completionVelocity);
  const estimatedDate = new Date(currentDate);
  estimatedDate.setDate(estimatedDate.getDate() + daysRemaining);
  return estimatedDate;
}
